/**
 * Blazor JS-interop bridge for the "Siri Wave" symbol — Siri-style GLSL shaders
 * rendered on a raw WebGL canvas.
 *
 * Exposed on window.App as:
 *  - App.siriWaveInit(elementId, options)    mount a canvas + shader into the container div
 *  - App.siriWaveUpdate(elementId, options)  re-init when variant / size / renderScale change
 *  - App.siriWaveDispose(elementId)          dispose the shader (WebGL context cleanup)
 *
 * Called from SiriWaveSymbol.razor.cs.
 *
 * Self-contained: a single fullscreen triangle drives a fragment shader, no
 * external WebGL library. The shaders are ported verbatim from the original
 * `siriWaveCore` / `siriFluidDotsCore` sources.
 */

export interface SiriWaveInteropOptions {
    /** Which shader to render: "wave" | "fluid-dots". */
    variant?: string;
    /** CSS display size of the square canvas, in px. */
    size?: number;
    /** Internal render resolution multiplier (lower = cheaper/blurrier). */
    renderScale?: number;
    /** What the symbol is doing: "idle" | "listening" | "talking" | "thinking". */
    mode?: string;
}

interface SiriWaveHandle {
    canvas: HTMLCanvasElement;
    gl: WebGLRenderingContext;
    program: WebGLProgram;
    vs: WebGLShader;
    fs: WebGLShader;
    buffer: WebGLBuffer | null;
    uResolution: WebGLUniformLocation | null;
    uTime: WebGLUniformLocation | null;
    uAmp: WebGLUniformLocation | null;
    uGlow: WebGLUniformLocation | null;
    raf: number;
    mode: string;
    start: number;
    variant: string;
    size: number;
    renderScale: number;
}

const mounts = new Map<string, SiriWaveHandle>();

/** A synthetic "voice" envelope (0..1): syllable-like pulses grouped into phrases with short pauses. */
function speechEnv(t: number): number {
    const syllable = Math.pow(Math.max(0, Math.sin(t * 9 + Math.sin(t * 2.3) * 2)), 1.3);
    const phrase = Math.min(1, Math.max(0, (Math.sin(t * 0.9 + Math.sin(t * 0.37) * 2) + 0.25) * 2.5));
    const body = 0.45 + 0.55 * Math.sin(t * 5.1 + 1) * Math.sin(t * 5.1 + 1);
    return Math.min(1, Math.max(0, syllable * body * phrase * 1.15));
}

const VERTEX_SHADER = `attribute vec2 aPos; void main(){ gl_Position=vec4(aPos,0.0,1.0); }`;

const WAVE_SHADER = `precision highp float;
uniform vec2 iResolution; uniform float iTime;
const float PI = 3.14159265359;
uniform float uAmp;
uniform float uGlow;
#define AMPLITUDE (0.32*uAmp)
const float FREQ        = 1.1;
const float ABER_FREQ   = 1.0;
const float SPEED       = 2.4;
const float WAVE_SCALE  = 0.6;
const float ABERRATION  = 2.6;
const float THICKNESS   = 3.0;
#define INTENSITY (2.*uGlow)
const float FALLOFF     = 1.7;
const float EDGE_MASK   = 0.4;
const float EDGE_INSET  = 0.0;
const float BAND_FILL   = 30000.0;
const float BAND_THICK  = 0.08;
const float SOFTNESS    = 2.5;
const float LOW_AMP     = 6.0;
const float LOW_INT     = 1.5;
const float MID_ABER    = 0.8;
const float MID_ABAMP   = 0.05;
const float MID_BAND    = 20.0;
const float MID_SOFT    = 0.4;
const float HIGH_ABER   = 0.5;
const float HIGH_ABAMP  = 0.06;
const float RESOLVED    = 1.0;
const float UNRES_SCALE = 0.14;

vec3 spectral4(int s){
    float x = float(s);
    return clamp(vec3(abs(x-3.0)-1.0, 2.0-abs(x-2.0), 2.0-abs(x-4.0)), 0.0, 1.0);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord){
    vec2 R = iResolution.xy;
    float aspect = R.x / R.y;
    vec2 p = (fragCoord + 0.5) * 2.0 / R - 1.0;
    p.x *= aspect;
    float yScreen = p.y;
    p /= max(WAVE_SCALE, 0.1);

    float t   = iTime;
    float low  = clamp(0.45 + 0.45*sin(t*0.8)*sin(t*0.37+1.0), 0.0, 1.0);
    float mid  = clamp(0.40 + 0.40*sin(t*1.7+2.0)*sin(t*0.53), 0.0, 1.0);
    float high = clamp(0.30 + 0.30*sin(t*2.9+4.0)*sin(t*0.71+2.0), 0.0, 1.0);

    float res   = clamp(RESOLVED, 0.0, 1.0);
    float drift = mod(t, 20.0*PI) * SPEED;

    float xN  = p.x / max(aspect, 1.0);
    float env = cos(PI*0.5 * min(abs(0.9*xN), 1.0));
    env *= env;

    float A1    = AMPLITUDE + 0.01*low*LOW_AMP;
    float A2    = A1 + mid*MID_ABAMP + high*HIGH_ABAMP;
    float AB    = (ABERRATION + mid*MID_ABER + high*HIGH_ABER)*res;
    float th    = mix(0.1, 0.01*THICKNESS, res);
    float inten = mix(0.1, 0.01*(INTENSITY + low*LOW_INT), res);
    float soft  = 0.01*res*max(0.0, SOFTNESS + mid*MID_SOFT);

    float dUnres = max(length(p) - mix(0.14, UNRES_SCALE, res), 0.0);
    float yMain = A1 * env * res * sin(p.x*FREQ + drift);

    float bandFillTh = max(BAND_THICK, 1e-4);
    float bandAmt    = 1e-4 * BAND_FILL * inten;
    vec3 num = vec3(0.0), den = vec3(0.0);
    for(int s = 0; s < 4; s++){
        vec3 hue = mix(vec3(1.0), spectral4(s), res);
        den += hue;
        float ab = mix(-AB, AB, float(s)/3.0);
        float yL = A2 * env * res * sin(p.x*ABER_FREQ + drift + ab);
        float d   = mix(dUnres, abs(p.y - yL), res);
        float lor = mix(1.0/(1.0 + (0.02*d)*(0.02*d)), 1.0, res);
        float line = inten / (sqrt(d*d + soft*soft) + th);
        float lo = min(yMain, yL), hi = max(yMain, yL);
        float dBand = max(0.0, max(p.y - hi, lo - p.y));
        float band  = bandAmt / (dBand + bandFillTh);
        num += hue * lor * (line + band);
    }
    vec3 col = num / den;

    float dM    = mix(dUnres, abs(p.y - yMain), res);
    float lorM  = mix(1.0/(1.0 + (0.02*dM)*(0.02*dM)), 1.0, res);
    float boost = (1.0 - res) * (14.0*low + 4.0);
    col += 0.5 * inten * (lorM + boost) / (sqrt(dM*dM + soft*soft) + th);

    col = pow(max(col, 0.0), vec3(1.5));
    float emT = clamp((abs(yScreen) - 1.0 + EDGE_INSET) / (-max(EDGE_MASK, 1e-4)), 0.0, 1.0);
    float em  = emT*emT*(3.0 - 2.0*emT);
    float gauss = exp(-pow(xN*FALLOFF, 2.0));
    col *= mix(1.0, em*gauss, res);
    col *= res;
    fragColor = vec4(col, 1.0);
}
void main(){ mainImage(gl_FragColor, gl_FragCoord.xy); }`;

const FLUID_DOTS_SHADER = `precision highp float;
uniform vec2 iResolution; uniform float iTime;
const float TAU = 6.28318530718;
const int   N   = 6;
const float SMOOTH_K = 0.08;
uniform float uGlow;
#define INTENSITY (0.0025*uGlow)
const float FALLOFF_P  = 1.35;
const float FADE_START = 0.02;
const float FADE_END   = 0.56;
const float ABERR = 0.005;
const vec3  SPECTRAL = vec3(0.0, 0.5, 1.0) * ABERR;
const float HUE_SPEED = 0.06;
const float COLOR_K   = 0.5;
const float SAT       = 0.01;
const float HUE_SPAN  = 0.667;
const float MERGE_PERIOD = 6.0;
const float T_MOVE   = 1.25;
const float STAGGER  = 0.33;
const float HOLD     = 0.0;
const float W = 4.6;
const float L = 3.2;
const float PIERCE  = 0.12;
const float RECOIL  = 0.035;
const float REC_LAG = 0.11;
const float GATHER_PERIOD = 12.0;
const float GATHER_START  = 9.2;
const float GATHER_HOLD   = 0.8;
const float GATHER_R      = 0.008;
const float GATHER_DIM    = 0.85;
const float GATHER_IN     = 1.8;
const float GATHER_IN_L   = 7.5;
const float BURST_W = 6.5;
const float BURST_L = 4.0;
const float CHARGE_T     = 0.30;
const float CHARGE_SHRK  = 0.18;
const float CHARGE_GLOW  = 0.35;
const float FLASH_GAIN   = 1.2;
const float FLASH_DECAY  = 7.0;

float hash11(float n){ return fract(sin(n*127.1 + 311.7)*43758.5453); }
float settleWL(float tau, float w, float l){
    if(tau <= 0.0) return 0.0;
    return 1.0 - exp(-l*tau)*cos(w*tau);
}
float settle(float tau){ return settleWL(tau, W, L); }
float settleCrit(float tau, float l){
    if(tau <= 0.0) return 0.0;
    return 1.0 - exp(-l*tau)*(1.0 + l*tau);
}
float smin(float a, float b, float k){
    float h = max(k - abs(a - b), 0.0) / k;
    return min(a, b) - h*h*k*0.25;
}
vec3 hue2rgb(float h){
    h = fract(h);
    float r = clamp(abs(h*6.0 - 3.0) - 1.0, 0.0, 1.0);
    float g = clamp(2.0 - abs(h*6.0 - 2.0), 0.0, 1.0);
    float b = clamp(2.0 - abs(h*6.0 - 4.0), 0.0, 1.0);
    return vec3(r, g, b);
}
float dotR(float fi, float seed, float t){
    return 0.036 + 0.010*sin(t*1.3 + seed*TAU) + 0.005*sin(t*2.4 + fi*1.3);
}
float dotSD(vec2 p, vec2 pos, float r, float t, float fi, float shapeDamp){
    vec2 d = p - pos;
    float sq = 0.075 * (0.5 + 0.5*sin(t*0.9 + fi*2.0)) * shapeDamp;
    float ca = cos(t*0.35 + fi), sa = sin(t*0.35 + fi);
    d = mat2(ca,-sa,sa,ca) * d;
    d *= vec2(1.0+sq, 1.0-sq);
    return length(d) - r;
}
vec3 scene(vec2 p, float t){
    float k  = floor(t/MERGE_PERIOD);
    float u  = fract(t/MERGE_PERIOD);
    float te = u * MERGE_PERIOD;
    float tg = mod(t, GATHER_PERIOD);
    float g  = settleCrit((tg - GATHER_START) * GATHER_IN, GATHER_IN_L)
             - settleWL(tg - GATHER_START - GATHER_HOLD, BURST_W, BURST_L);
    float gC = clamp(g, 0.0, 1.0);
    float tb     = tg - (GATHER_START + GATHER_HOLD);
    float charge = smoothstep(-CHARGE_T, 0.0, min(tb, 0.0)) * gC;
    float flash  = tb > 0.0 ? exp(-tb * FLASH_DECAY) : 0.0;
    float gBright = mix(1.0, GATHER_DIM, gC) * (1.0 + CHARGE_GLOW*charge + FLASH_GAIN*flash);
    vec3  total3 = vec3(1e5);
    vec3  cAcc   = vec3(0.0);
    float wAcc   = 1e-6;
    for(int i=0; i<N; i++){
        float fi   = float(i);
        float seed = hash11(fi);
        float ang = fi/float(N)*TAU + t*0.35;
        vec2 dir  = vec2(cos(ang), sin(ang));
        float R = 0.17 + 0.010*sin(t*1.0) + 0.007*sin(t*1.3 + seed*TAU);
        float pairId   = mod(fi, 3.0);
        float moverLow = mod(k + pairId, 2.0);
        float isMover  = (fi < 2.5) ? step(moverLow, 0.5) : step(0.5, moverLow);
        float goStart  = pairId * STAGGER;
        float retStart = 3.0*STAGGER + HOLD + pairId * STAGGER;
        float m   = (settle(te - goStart)           - settle(te - retStart))           * isMover;
        float rec = (settle(te - goStart - REC_LAG) - settle(te - retStart - REC_LAG)) * (1.0 - isMover);
        float rSelf = dotR(fi, seed, t);
        rSelf = mix(rSelf, 0.036, gC);
        rSelf *= 1.0 - CHARGE_SHRK * charge;
        float fj    = mod(fi + 3.0, 6.0);
        float rPart = dotR(fj, hash11(fj), t);
        float deep   = -(R + RECOIL) - PIERCE * rPart;
        float radial = mix(R, deep, m) + RECOIL * rec;
        radial = mix(radial, GATHER_R, g);
        vec2  pos    = radial * dir;
        float sdR = dotSD(p - SPECTRAL.r*dir, pos, rSelf, t, fi, 1.0 - gC);
        float sdG = dotSD(p - SPECTRAL.g*dir, pos, rSelf, t, fi, 1.0 - gC);
        float sdB = dotSD(p - SPECTRAL.b*dir, pos, rSelf, t, fi, 1.0 - gC);
        total3 = vec3( smin(total3.r, sdR, SMOOTH_K),
                       smin(total3.g, sdG, SMOOTH_K),
                       smin(total3.b, sdB, SMOOTH_K) );
        float hue = fract(fi/float(N) + t*HUE_SPEED) * HUE_SPAN;
        vec3 dotCol = mix(vec3(1.0), hue2rgb(hue), SAT);
        float w = exp(-sdG * COLOR_K);
        cAcc += w * dotCol;
        wAcc += w;
    }
    vec3 sd3    = max(total3, vec3(0.0)) + 1e-4;
    vec3 core3  = clamp(INTENSITY / pow(sd3, vec3(FALLOFF_P)), 0.0, 1.0);
    vec3 edge3  = 1.0 - smoothstep(vec3(FADE_START), vec3(FADE_END), sd3);
    vec3 bright = core3 * edge3 * gBright;
    return bright * (cAcc / wAcc);
}
void mainImage(out vec4 fragColor, in vec2 fragCoord){
    vec2 res = iResolution.xy;
    vec2 p = (2.0*fragCoord - res) / min(res.x, res.y);
    float t = iTime;
    p /= 1.0 + 0.03*sin(t*1.0);
    vec3 col = scene(p, t);
    col *= 1.0 + 0.05*sin(t*1.0 + 1.0);
    col = pow(col, vec3(1.0/1.2));
    col = min(col, 1.0);
    float n = fract(sin(dot(fragCoord, vec2(12.9898,78.233)))*43758.5453);
    col += (n - 0.5)/255.0;
    fragColor = vec4(col, 1.0);
}
void main(){ mainImage(gl_FragColor, gl_FragCoord.xy); }`;

const FRAGMENT_SHADERS: Record<string, string> = {
    wave: WAVE_SHADER,
    "fluid-dots": FLUID_DOTS_SHADER,
};

function buildHandle(elementId: string, options: SiriWaveInteropOptions): SiriWaveHandle | null {
    const container = document.getElementById(elementId);
    if (!container) return null;

    const variant = options.variant ?? "wave";
    const size = options.size ?? 360;
    const renderScale = options.renderScale ?? 0.75;

    const canvas = document.createElement("canvas");
    canvas.className = "siri-wave-canvas";
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    container.appendChild(canvas);

    const gl =
        canvas.getContext("webgl") ??
        (canvas.getContext("experimental-webgl") as WebGLRenderingContext | null);
    if (!gl) {
        console.error("[SiriWave] WebGL is not supported on this device/browser.");
        return null;
    }

    const compile = (type: number, src: string): WebGLShader => {
        const shader = gl.createShader(type);
        if (!shader) throw new Error("shader creation failed");
        gl.shaderSource(shader, src);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            const log = gl.getShaderInfoLog(shader);
            gl.deleteShader(shader);
            throw new Error(log ?? "shader compile error");
        }
        return shader;
    };

    const program = gl.createProgram();
    if (!program) throw new Error("program creation failed");
    const vs = compile(gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADERS[variant] ?? WAVE_SHADER);
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(program, "aPos");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uResolution = gl.getUniformLocation(program, "iResolution");
    const uTime = gl.getUniformLocation(program, "iTime");
    const uAmp = gl.getUniformLocation(program, "uAmp");
    const uGlow = gl.getUniformLocation(program, "uGlow");

    const dim = Math.round(size * renderScale);
    canvas.width = dim;
    canvas.height = dim;
    gl.viewport(0, 0, dim, dim);

    const start = typeof performance !== "undefined" ? performance.now() : Date.now();

    const handle: SiriWaveHandle = {
        canvas,
        gl,
        program,
        vs,
        fs,
        buffer,
        uResolution,
        uTime,
        uAmp,
        uGlow,
        raf: 0,
        mode: options.mode ?? "idle",
        start,
        variant,
        size,
        renderScale,
    };

    // Each mode eases toward its own speed / amplitude / glow, so a switch glides instead of jumping.
    //  idle      slow, shallow, dim      listening  steady, medium
    //  talking   fast and loud, the wave swells with every syllable
    //  thinking  slow breathing wave (or, for the dots, a fast gather-and-scatter)
    const cur = { speed: 0.7, amp: 0.45, glow: 0.8 };
    let tAcc = 0;
    let last = start;
    const frame = (): void => {
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        const dt = Math.min(0.05, (now - last) / 1000);
        last = now;
        const clock = (now - start) / 1000;
        const sp = speechEnv(clock);
        const isWave = variant !== "fluid-dots";
        let tgt: { speed: number; amp: number; glow: number };
        switch (handle.mode) {
            case "listening": tgt = { speed: isWave ? 1.0 : 1.0, amp: 0.9, glow: 1.0 }; break;
            case "talking": tgt = { speed: isWave ? 2.0 : 1.8, amp: 0.6 + 0.9 * sp, glow: 1.2 + 0.5 * sp }; break;
            case "thinking": tgt = { speed: isWave ? 0.5 : 2.4, amp: 0.55 * (0.8 + 0.2 * Math.sin(clock * 1.2)), glow: 1.1 }; break;
            default: tgt = { speed: isWave ? 0.7 : 0.6, amp: 0.45, glow: 0.8 }; break;
        }
        const k = 1 - Math.exp(-dt * 4);
        cur.speed += (tgt.speed - cur.speed) * k;
        cur.amp += (tgt.amp - cur.amp) * (1 - Math.exp(-dt * 12));
        cur.glow += (tgt.glow - cur.glow) * k;
        tAcc += dt * cur.speed;
        const t = tAcc;
        gl.uniform2f(uResolution, dim, dim);
        gl.uniform1f(uTime, t);
        gl.uniform1f(uAmp, cur.amp);
        gl.uniform1f(uGlow, cur.glow);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        handle.raf = requestAnimationFrame(frame);
    };
    frame();

    return handle;
}

export function siriWaveInit(elementId: string, options: SiriWaveInteropOptions): void {
    siriWaveDispose(elementId);

    try {
        const handle = buildHandle(elementId, options);
        if (handle) {
            mounts.set(elementId, handle);
        }
    } catch (error) {
        console.error("[SiriWave] Failed to initialize shader:", error);
    }
}

export function siriWaveUpdate(elementId: string, options: SiriWaveInteropOptions): void {
    const handle = mounts.get(elementId);
    if (!handle) return;

    handle.mode = options.mode ?? "idle";   // the mode never needs a re-init, the frame loop eases to it

    const variant = options.variant ?? "wave";
    const size = options.size ?? 360;
    const renderScale = options.renderScale ?? 0.75;

    // All parameters are fixed at mount time (shader choice + canvas resolution),
    // so any change requires re-initialization.
    if (handle.variant !== variant || handle.size !== size || handle.renderScale !== renderScale) {
        siriWaveInit(elementId, options);
    }
}

export function siriWaveDispose(elementId: string): void {
    const handle = mounts.get(elementId);
    if (!handle) return;

    try {
        cancelAnimationFrame(handle.raf);
        handle.gl.deleteProgram(handle.program);
        handle.gl.deleteShader(handle.vs);
        handle.gl.deleteShader(handle.fs);
        handle.gl.deleteBuffer(handle.buffer);
        handle.canvas.remove();
    } catch (error) {
        console.error("[SiriWave] Failed to dispose shader:", error);
    }
    mounts.delete(elementId);
}

// app.ts (which creates window.App) may run before or after this module inside the same esbuild
// bundle depending on the order of the entry file's imports. Attach immediately when possible and
// always re-attach on the next tick, once the whole bundle has finished executing.
const attachToApp = (): void => {
    const w = window as any;
    const app = w.App ?? (w.App = {});
    app.siriWaveInit = siriWaveInit;
    app.siriWaveUpdate = siriWaveUpdate;
    app.siriWaveDispose = siriWaveDispose;
};

attachToApp();
setTimeout(attachToApp, 0);

export {};
